import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState, StatusPill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Eye, MagnifyingGlass, Minus, PencilSimple, Plus, Trash, UploadSimple,
} from "@phosphor-icons/react";
import LeadImportDialog from "@/components/LeadImportDialog";

const DROP_RATIO_TOOLTIP =
  "List Drop Ratio = (Dropped Leads ÷ Total Leads) × 100. " +
  "Dropped Leads = Total − NEW − Successful (ANSWERED, SALE, COMPLETED, XFER, NI).";

function ListDropRatio({ row }) {
  const total = Number(row?.total_leads ?? row?.lead_count ?? 0);
  const dropped = Number(row?.dropped_leads ?? row?.technical_drops ?? 0);
  const newLeads = Number(row?.new_leads ?? 0);
  const successful = Number(row?.successful_leads ?? 0);
  const undefinedRatio = total <= 0 || row?.drop_ratio_undefined === true;
  const display = undefinedRatio
    ? "—"
    : (row?.drop_ratio_display || (row?.drop_ratio_pct != null ? `${Number(row.drop_ratio_pct).toFixed(2)}%` : "—"));
  const detail = total > 0
    ? `${dropped} dropped ÷ ${total} total (NEW ${newLeads}, successful ${successful}) = ${display}`
    : "No leads — ratio undefined (not 0%).";
  const high = !undefinedRatio && Number(row?.drop_ratio_pct) >= 50;
  return (
    <span
      data-testid={`list-drop-ratio-${row?.id}`}
      title={`${DROP_RATIO_TOOLTIP} ${detail}`}
      className={`tabular-nums font-semibold ${undefinedRatio ? "text-muted-foreground" : high ? "text-amber-700 dark:text-amber-300" : "text-foreground"}`}
    >
      {display}
      {!undefinedRatio && (
        <span className="block text-[10px] font-normal text-muted-foreground leading-tight">
          {dropped}/{total}
        </span>
      )}
    </span>
  );
}

function qualityTitle(q) {
  if (!q) return "No quality data yet";
  const f = q.factors || {};
  const bits = [
    `Answered ${f.answered ?? 0}`,
    `Failed ${f.failed ?? 0}`,
    `Error numbers ${f.error ?? 0}`,
    `AMD ${f.amd ?? 0}`,
  ];
  if (f.attempted) bits.unshift(`${f.attempted} dialed`);
  const head = q.label || (q.sign === "positive" ? "Good" : q.sign === "negative" ? "Poor" : "Unknown");
  return `${head} — ${bits.join(" · ")}`;
}

function ListQualitySign({ quality }) {
  const sign = quality?.sign || "neutral";
  const title = qualityTitle(quality);
  if (sign === "positive") {
    return (
      <span
        className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-emerald-100 text-emerald-700"
        title={title}
        data-testid="list-quality"
        data-sign="positive"
        aria-label={title}
      >
        <Plus className="w-4 h-4" weight="bold" />
      </span>
    );
  }
  if (sign === "negative") {
    return (
      <span
        className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-red-100 text-red-700"
        title={title}
        data-testid="list-quality"
        data-sign="negative"
        aria-label={title}
      >
        <Minus className="w-4 h-4" weight="bold" />
      </span>
    );
  }
  return (
    <span
      className="inline-flex h-7 min-w-[1.75rem] items-center justify-center rounded-full bg-muted text-muted-foreground text-xs"
      title={title}
      data-testid="list-quality"
      data-sign="neutral"
      aria-label={title}
    >
      —
    </span>
  );
}

export default function CustomerLists() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", status: "active" });
  const [saving, setSaving] = useState(false);
  const [importDlg, setImportDlg] = useState(null);
  const [createAndImport, setCreateAndImport] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customer-leads/lists", { params: q.trim() ? { q: q.trim() } : {} });
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setLoading(false);
    }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm({ name: "", description: "", status: "active" });
    setCreateAndImport(false);
    setDlg("new");
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (dlg === "new") {
        const { data } = await api.post("/customer-leads/lists", form);
        toast.success("List created");
        setDlg(null);
        if (createAndImport && data?.id) {
          setImportDlg(data);
        } else if (data?.id) {
          navigate(`/lists/${data.id}`);
          return;
        }
        load();
      } else {
        await api.patch(`/customer-leads/lists/${dlg.id}`, form);
        toast.success("List updated");
        setDlg(null);
        load();
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (row?.list_kind === "inbound" || row?.deletable === false) {
      toast.error("The Inbound list cannot be deleted.");
      return;
    }
    if (!window.confirm(`Delete list “${row.name}”? Leads stay but are unassigned.`)) return;
    try {
      await api.delete(`/customer-leads/lists/${row.id}`);
      toast.success("List deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    }
  };

  return (
    <div className="space-y-6" data-testid="customer-lists-page">
      <PageHeader title="Lists" subtitle="Contact lists for dialing. Inbound is a system list for incoming callers and cannot be edited or assigned to campaigns." />
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="relative">
          <MagnifyingGlass className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input className="pl-8 w-56" placeholder="Search lists…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Button className="rounded-lg" onClick={openNew} data-testid="list-add">
          <Plus className="w-4 h-4 mr-1.5" /> New list
        </Button>
      </div>
      {loading ? <p className="text-sm text-muted-foreground">Loading…</p>
        : rows.length === 0 ? <EmptyState title="No lists" description="Create a list, then import or add leads." />
          : (
            <div className="rounded-xl border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Campaigns</TableHead>
                    <TableHead className="text-right">Leads</TableHead>
                    <TableHead className="text-center" title={DROP_RATIO_TOOLTIP}>Drop Ratio</TableHead>
                    <TableHead className="text-center">Quality</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-48" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id} className="cursor-pointer hover:bg-muted/40" onClick={() => navigate(`/lists/${r.id}`)} data-testid={`list-row-${r.id}`}>
                      <TableCell>
                        <div className="font-medium flex items-center gap-2">
                          <span>{r.name}</span>
                          {(r.list_kind === "inbound" || r.system_list) && (
                            <span
                              className="inline-flex rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
                              data-testid={`list-system-badge-${r.id}`}
                            >
                              System
                            </span>
                          )}
                        </div>
                        {r.description && <div className="text-xs text-muted-foreground">{r.description}</div>}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {(r.campaigns || []).length === 0 ? (
                          <span className="text-xs text-muted-foreground">None</span>
                        ) : (
                          <div className="flex flex-wrap gap-1 max-w-xs">
                            {(r.campaigns || []).slice(0, 4).map((c) => (
                              <span
                                key={c.id}
                                className="inline-flex items-center rounded border px-1.5 py-0.5 text-[11px] bg-muted/50"
                                title={c.name}
                              >
                                {c.name}
                              </span>
                            ))}
                            {(r.campaigns || []).length > 4 && (
                              <span className="text-[11px] text-muted-foreground">
                                +{r.campaigns.length - 4}
                              </span>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{r.lead_count ?? 0}</TableCell>
                      <TableCell className="text-center">
                        <ListDropRatio row={r} />
                      </TableCell>
                      <TableCell className="text-center">
                        <ListQualitySign quality={r.quality} />
                      </TableCell>
                      <TableCell><StatusPill status={r.status} /></TableCell>
                      <TableCell className="text-right space-x-1" onClick={(e) => e.stopPropagation()}>
                        <Button size="sm" variant="ghost" title="View detail" onClick={() => navigate(`/lists/${r.id}`)} data-testid="list-view">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Import"
                          onClick={() => setImportDlg(r)}
                        >
                          <UploadSimple className="w-4 h-4" />
                        </Button>
                        {r.editable !== false && r.list_kind !== "inbound" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setForm({ name: r.name, description: r.description || "", status: r.status });
                              setDlg(r);
                            }}
                            data-testid={`list-edit-${r.id}`}
                          >
                            <PencilSimple className="w-4 h-4" />
                          </Button>
                        )}
                        {r.deletable !== false && r.list_kind !== "inbound" && (
                          <Button size="sm" variant="ghost" onClick={() => remove(r)} data-testid={`list-delete-${r.id}`}>
                            <Trash className="w-4 h-4 text-destructive" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

      <Dialog open={!!dlg} onOpenChange={(v) => !v && setDlg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{dlg === "new" ? "New list" : "Edit list"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="list-name" /></div>
            <div className="space-y-1.5"><Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            {dlg !== "new" && (
              <div className="space-y-1.5"><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {dlg === "new" && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={createAndImport}
                  onChange={(e) => setCreateAndImport(e.target.checked)}
                />
                Import file right after creating
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setDlg(null)}>Cancel</Button>
            <Button className="rounded-lg" onClick={save} disabled={saving} data-testid="list-save">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LeadImportDialog
        open={!!importDlg}
        onOpenChange={(v) => !v && setImportDlg(null)}
        list={importDlg}
        onImported={load}
      />
    </div>
  );
}
